// Werkdagen-utility met statische NL feestdagenlijst.
// Houdt NL_HOLIDAYS up-to-date per jaar — addBusinessDays/isBusinessDay throwen
// als het jaar van de input niet in de lijst staat (forceert update i.p.v. stille bug).

const NL_HOLIDAYS = {
    2020: [
        '2020-01-01',
        '2020-04-13', // Paasmaandag
        '2020-04-27', // Koningsdag (ma)
        '2020-05-21', // Hemelvaart
        '2020-06-01', // Pinkstermaandag
        '2020-12-25',
        '2020-12-26',
    ],
    2021: [
        '2021-01-01',
        '2021-04-05', // Paasmaandag
        '2021-04-27', // Koningsdag (di)
        '2021-05-13', // Hemelvaart
        '2021-05-24', // Pinkstermaandag
        '2021-12-25',
        '2021-12-26',
    ],
    2022: [
        '2022-01-01',
        '2022-04-18', // Paasmaandag
        '2022-04-27', // Koningsdag (wo)
        '2022-05-26', // Hemelvaart
        '2022-06-06', // Pinkstermaandag
        '2022-12-25',
        '2022-12-26',
    ],
    2023: [
        '2023-01-01',
        '2023-04-10', // Paasmaandag
        '2023-04-27', // Koningsdag (do)
        '2023-05-18', // Hemelvaart
        '2023-05-29', // Pinkstermaandag
        '2023-12-25',
        '2023-12-26',
    ],
    2024: [
        '2024-01-01', // Nieuwjaar
        '2024-04-01', // Paasmaandag
        '2024-04-27', // Koningsdag (zaterdag)
        '2024-05-09', // Hemelvaart
        '2024-05-20', // Pinkstermaandag
        '2024-12-25', // Eerste kerstdag
        '2024-12-26', // Tweede kerstdag
    ],
    2025: [
        '2025-01-01',
        '2025-04-21', // Paasmaandag
        '2025-04-27', // Koningsdag (zondag, in praktijk gevierd op zaterdag 26 april — beide weekend dus geen effect)
        '2025-05-29', // Hemelvaart
        '2025-06-09', // Pinkstermaandag
        '2025-12-25',
        '2025-12-26',
    ],
    2026: [
        '2026-01-01',
        '2026-04-06', // Paasmaandag
        '2026-04-27', // Koningsdag (maandag)
        '2026-05-14', // Hemelvaart
        '2026-05-25', // Pinkstermaandag
        '2026-12-25',
        '2026-12-26',
    ],
    2027: [
        '2027-01-01',
        '2027-03-29', // Paasmaandag
        '2027-04-27', // Koningsdag (dinsdag)
        '2027-05-06', // Hemelvaart
        '2027-05-17', // Pinkstermaandag
        '2027-12-25',
        '2027-12-26',
    ],
    2028: [
        '2028-01-01',
        '2028-04-17', // Paasmaandag
        '2028-04-27', // Koningsdag (donderdag)
        '2028-05-25', // Hemelvaart
        '2028-06-05', // Pinkstermaandag
        '2028-12-25',
        '2028-12-26',
    ],
    2029: [
        '2029-01-01',
        '2029-04-02', // Paasmaandag
        '2029-04-27', // Koningsdag (vrijdag)
        '2029-05-10', // Hemelvaart
        '2029-05-21', // Pinkstermaandag
        '2029-12-25',
        '2029-12-26',
    ],
    2030: [
        '2030-01-01',
        '2030-04-22', // Paasmaandag
        '2030-04-27', // Koningsdag (zaterdag)
        '2030-05-30', // Hemelvaart
        '2030-06-10', // Pinkstermaandag
        '2030-12-25',
        '2030-12-26',
    ],
};

const HOLIDAY_SETS = Object.fromEntries(
    Object.entries(NL_HOLIDAYS).map(([year, list]) => [year, new Set(list)])
);

const formatYmd = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const assertYearKnown = (year) => {
    if (!HOLIDAY_SETS[year]) {
        throw new Error(
            `businessDays: no holiday list for year ${year}. ` +
            `Update NL_HOLIDAYS in src/utils/businessDays.js before continuing.`
        );
    }
};

const isBusinessDay = (date) => {
    assertYearKnown(date.getFullYear());
    const day = date.getDay();
    if (day === 0 || day === 6) return false; // Sun=0, Sat=6
    return !HOLIDAY_SETS[date.getFullYear()].has(formatYmd(date));
};

const addBusinessDays = (date, n) => {
    if (n === 0) return new Date(date.getTime());
    const step = n > 0 ? 1 : -1;
    let remaining = Math.abs(n);
    const result = new Date(date.getTime());
    while (remaining > 0) {
        result.setDate(result.getDate() + step);
        if (isBusinessDay(result)) remaining -= 1;
    }
    return result;
};

const businessDaysBetween = (d1, d2) => {
    if (d1.getTime() === d2.getTime()) return 0;
    const forward = d2.getTime() > d1.getTime();
    const start = forward ? d1 : d2;
    const end = forward ? d2 : d1;
    let count = 0;
    const cursor = new Date(start.getTime());
    cursor.setHours(12, 0, 0, 0);
    const target = new Date(end.getTime());
    target.setHours(12, 0, 0, 0);
    while (cursor.getTime() < target.getTime()) {
        cursor.setDate(cursor.getDate() + 1);
        if (isBusinessDay(cursor)) count += 1;
    }
    return forward ? count : -count;
};

const businessDaysSince = (input) => {
    if (!input) return null;
    let date;
    if (input instanceof Date) {
        date = input;
    } else if (typeof input.toDate === 'function') {
        date = input.toDate();
    } else {
        return null;
    }
    return businessDaysBetween(date, new Date());
};

export {
    NL_HOLIDAYS,
    isBusinessDay,
    addBusinessDays,
    businessDaysBetween,
    businessDaysSince,
};
